import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Sparkles, Leaf, HeartHandshake, ShieldCheck } from "lucide-react";
import { Seo } from "@/components/Seo";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { CallButton, WhatsAppButton } from "@/components/CTAs";
import { ABOUT_INTERIOR, ABOUT_INTERIOR_2 } from "@/data/site";

const VALUES = [
  { icon: Sparkles, title: "Authentic Therapies", text: "Every service follows its origin — Balinese from Bali, Thai from Wat Pho tradition — taught properly, never diluted." },
  { icon: Leaf, title: "Pure Ingredients", text: "Cold-pressed oils, mineral salts and botanicals we would happily use on our own families." },
  { icon: HeartHandshake, title: "Mindful Pace", text: "No assembly-line bookings. Time between sessions means your therapist is present, not rushed." },
  { icon: ShieldCheck, title: "Quiet Hygiene", text: "Fresh linen, sanitised suites and a jacuzzi drawn new for every guest — as a standard, not a promise." },
];

export default function About() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);

  return (
    <main data-testid="about-page" className="pb-16 md:pb-0">
      <Seo
        title="About Us | Belong Saloon & Spa, Suchitra Hyderabad"
        description="The story of Belong Saloon & Spa — a sanctuary of authentic body massage and Jacuzzi hydrotherapy in Suchitra, Quthbullapur, Hyderabad."
      />

      <section ref={ref} className="relative flex min-h-[70vh] items-end overflow-hidden">
        <motion.div style={{ y }} className="absolute inset-0">
          <img src={ABOUT_INTERIOR} alt="Inside Belong Saloon and Spa — the reception lounge with gold lotus branding" className="h-[120%] w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-obsidian/70 via-obsidian/50 to-obsidian" />
        </motion.div>
        <div className="relative z-10 mx-auto w-full max-w-7xl px-5 pb-20 pt-44 sm:px-8">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}>
            <Eyebrow className="mb-4">About Belong</Eyebrow>
            <h1 className="max-w-3xl font-serif-display text-4xl leading-tight text-cream sm:text-6xl">
              A sanctuary above <span className="italic text-gold-gradient">the everyday</span>
            </h1>
          </motion.div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-32">
        <div className="grid items-center gap-14 lg:grid-cols-2">
          <Reveal>
            <Eyebrow className="mb-4">Our Story</Eyebrow>
            <h2 className="font-serif-display text-3xl leading-tight text-cream sm:text-4xl">
              Belong began with a simple observation
            </h2>
            <div className="mt-6 space-y-5 text-base leading-relaxed text-stone-400">
              <p>
                Suchitra has no shortage of noise — traffic, deadlines, screens that never sleep. What it lacked
                was a place built for the opposite: warm light, unhurried hands, and therapies practised the way
                they were meant to be.
              </p>
              <p>
                So we built one, on the first floor above Quthbullapur Main Road. No salon chairs, no rush,
                no noise — only body massage and hydrotherapy, seven services deep, delivered by certified
                therapists in private candlelit suites.
              </p>
              <p>
                The name is the intention: an hour at Belong should feel like returning somewhere you were
                always meant to be.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="gold-ring overflow-hidden rounded-2xl">
              <img src={ABOUT_INTERIOR_2} alt="Spa ambience with candles, towels and orchids at Belong Spa" loading="lazy" className="h-[460px] w-full object-cover" />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="border-y border-gold/10 bg-ember/50 py-24 sm:py-32">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal className="mb-14 max-w-2xl">
            <Eyebrow className="mb-4">What We Stand By</Eyebrow>
            <h2 className="font-serif-display text-3xl text-cream sm:text-4xl">
              Four pillars, no shortcuts
            </h2>
          </Reveal>
          <div className="grid gap-6 sm:grid-cols-2">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.08}>
                <div data-testid={`value-${i}`} className="gold-ring gold-ring-hover flex h-full gap-5 rounded-2xl bg-coal p-7">
                  <v.icon size={26} className="mt-1 shrink-0 text-gold" />
                  <div>
                    <h3 className="font-serif-display text-xl text-cream">{v.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-stone-400">{v.text}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 py-24 text-center sm:px-8 sm:py-32">
        <Reveal>
          <h2 className="mx-auto max-w-2xl font-serif-display text-3xl leading-tight text-cream sm:text-5xl">
            Come as you are. Leave as you <span className="italic text-gold-gradient">should feel.</span>
          </h2>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <CallButton testid="about-call-button" />
            <WhatsAppButton testid="about-whatsapp-button" />
          </div>
        </Reveal>
      </section>
    </main>
  );
}
